import React, { useEffect, useState } from 'react';
import { FaCalendarAlt, FaMapMarkerAlt, FaTicketAlt, FaExternalLinkAlt, FaAward, FaBuilding } from 'react-icons/fa';
import autogestionService, { HomeWidgetResponse } from '../../services/autogestionService';

export const CongresosHomeWidget: React.FC = () => {
  const [data, setData] = useState<HomeWidgetResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    autogestionService.getHomeWidget()
      .then(res => {
        setData(res);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message || 'Error al cargar eventos');
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="bg-white dark:bg-boxdark p-4 md:p-6 rounded-2xl border border-gray shadow-sm animate-pulse">
        <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-1/4 mb-4"></div>
        <div className="h-20 bg-slate-100 dark:bg-slate-800 rounded-xl mb-3"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-white dark:bg-boxdark p-4 md:p-6 rounded-2xl border border-gray shadow-sm">
        <p className="text-title-sm dark:text-white mb-2">Congresos y Eventos</p>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
          <div>
            <div className="inline-flex items-center space-x-2 text-xs font-medium text-secondary mb-1">
              <FaAward className="text-amber-500" />
              <span>Eventos Institucionales</span>
            </div>
            <p className="text-secondary text-sm">Consultá información sobre congresos y certificados de asistencia.</p>
          </div>
          <a
            href="/mi-autogestion/entradas"
            className="inline-flex items-center space-x-2 border border-primario-celeste-800 text-primario-celeste-800 hover:bg-primario-celeste-800 hover:text-white transition duration-150 px-4 py-2 rounded font-semibold text-sm self-start sm:self-auto"
          >
            <FaTicketAlt />
            <span>Ver Mis Entradas</span>
          </a>
        </div>
      </div>
    );
  }

  // Estado: Sin eventos activos
  if (!data.tieneEventoActivo || !data.evento) {
    return (
      <div className="bg-white dark:bg-boxdark p-4 md:p-6 rounded-2xl border border-gray shadow-sm">
        <p className="text-title-sm dark:text-white mb-2">Congresos y Eventos</p>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
          <div>
            <div className="inline-flex items-center space-x-2 text-xs font-medium text-secondary mb-1">
              <FaAward className="text-amber-500" />
              <span>Eventos Institucionales</span>
            </div>
            <p className="text-secondary text-sm">No hay eventos activos programados en este momento.</p>
          </div>
          <a
            href="/mi-autogestion/entradas"
            className="inline-flex items-center space-x-2 border border-primario-celeste-800 text-primario-celeste-800 hover:bg-primario-celeste-800 hover:text-white transition duration-150 px-4 py-2 rounded font-semibold text-sm self-start sm:self-auto"
          >
            <FaTicketAlt />
            <span>Ver Historial y Certificados</span>
          </a>
        </div>
      </div>
    );
  }

  const { evento } = data;

  return (
    <div className="bg-white dark:bg-boxdark p-4 md:p-6 rounded-2xl border border-gray shadow-sm space-y-4">
      <div className="flex items-center justify-between border-b border-gray-2 dark:border-strokedark pb-3">
        <p className="text-title-sm dark:text-white">Congresos y Eventos</p>
        <span className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
          Evento Destacado
        </span>
      </div>

      {evento.imagenBannerUrl && (
        <div className="overflow-hidden rounded-xl max-h-48 w-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center border border-gray-2 dark:border-strokedark">
          <img
            src={evento.imagenBannerUrl}
            alt={`Banner de ${evento.nombre}`}
            className="w-full h-48 object-cover"
            onError={(e) => {
              (e.target as HTMLElement).style.display = 'none';
            }}
          />
        </div>
      )}

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div className="space-y-3 flex-1">
          <h3 className="text-lg font-bold text-primario-azul-900 dark:text-white">{evento.nombre}</h3>

          <div className="flex flex-wrap gap-4 text-sm text-secondary">
            <div className="flex items-center space-x-1.5">
              <FaCalendarAlt className="text-primario-celeste-800" />
              <span>{new Date(evento.fechaDesde).toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
            </div>
            <div className="flex items-center space-x-1.5">
              <FaMapMarkerAlt className="text-rose-500" />
              <span>{evento.lugar}</span>
            </div>
          </div>

          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-xl p-3 flex items-start space-x-3 text-xs text-amber-900 dark:text-amber-200">
            <FaBuilding className="text-amber-600 text-base flex-shrink-0 mt-0.5" />
            <p>{evento.avisoAdquisicion}</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row lg:flex-col gap-3 justify-center min-w-[200px]">
          <a
            href="/mi-autogestion/entradas"
            className="inline-flex items-center justify-center space-x-2 bg-primario-celeste-800 hover:bg-primario-azul-900 text-white font-semibold py-2.5 px-4 rounded text-sm transition duration-150 shadow-sm"
          >
            <FaTicketAlt />
            <span>Mis Entradas</span>
          </a>

          {evento.urlMicrositio && (
            <a
              href={evento.urlMicrositio}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center space-x-2 border border-primario-celeste-800 text-primario-celeste-800 hover:bg-primario-celeste-800 hover:text-white font-semibold py-2.5 px-4 rounded text-sm transition duration-150"
            >
              <span>Ver Micrositio</span>
              <FaExternalLinkAlt className="text-xs" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};
